import { GQL } from '@aline/graphql';
import { format, isValid, parse, parseISO } from 'date-fns';

import { getISO8601DateString } from './dates';

/**
 * Every date-fns format string used by a picked GQL.DateFieldFormat.
 * A new format is a one-line addition here plus a new enum member.
 */
const FORMAT_STRINGS: Record<GQL.DateFieldFormat, string> = {
  [GQL.DateFieldFormat.MmmmDYyyy]: 'MMMM d, yyyy',
  [GQL.DateFieldFormat.MmmmDoYyyy]: 'MMMM do, yyyy',
  [GQL.DateFieldFormat.DoMmmmYyyy]: 'do MMMM yyyy',
  [GQL.DateFieldFormat.DMmmmYyyy]: 'd MMMM yyyy',
  [GQL.DateFieldFormat.YyyyMmmmD]: 'yyyy MMMM d',
  [GQL.DateFieldFormat.MmmDYyyy]: 'MMM d, yyyy',
  [GQL.DateFieldFormat.MmmDdYyyyDash]: 'MMM-dd-yyyy',
  [GQL.DateFieldFormat.DdMmmYyyyDash]: 'dd-MMM-yyyy',
  [GQL.DateFieldFormat.DdMmmYyDash]: 'dd-MMM-yy',
  [GQL.DateFieldFormat.YyyyMmmDdDash]: 'yyyy-MMM-dd',
  [GQL.DateFieldFormat.MmDdYyyySlash]: 'MM/dd/yyyy',
  [GQL.DateFieldFormat.MDYyyySlash]: 'M/d/yyyy',
  [GQL.DateFieldFormat.MmDdYyyyDash]: 'MM-dd-yyyy',
  [GQL.DateFieldFormat.MmDdYySlash]: 'MM/dd/yy',
  [GQL.DateFieldFormat.DdMmYyyySlash]: 'dd/MM/yyyy',
  [GQL.DateFieldFormat.DMYyyySlash]: 'd/M/yyyy',
  [GQL.DateFieldFormat.DdMmYyyyDash]: 'dd-MM-yyyy',
  [GQL.DateFieldFormat.DdMmYyDash]: 'dd-MM-yy',
  [GQL.DateFieldFormat.DdMmYyyyDot]: 'dd.MM.yyyy',
  [GQL.DateFieldFormat.YyyyMmDd]: 'yyyy-MM-dd',
  [GQL.DateFieldFormat.YyyyMmDdSlash]: 'yyyy/MM/dd',
};

/**
 * Fallback for fields that predate the per-field format setting. Matches the
 * legacy hard-coded `'MMMM d, yyyy'` so existing filled text keeps rendering
 * and round-tripping the same as before.
 */
export const DEFAULT_DATE_FIELD_FORMAT = GQL.DateFieldFormat.MmmmDYyyy;

const MIN_PARSED_YEAR = 1000;

const PREVIEW_DATE = new Date(2026, 0, 18);

export function getDateFieldFormatString(fmt: GQL.DateFieldFormat | null | undefined): string {
  return FORMAT_STRINGS[fmt ?? DEFAULT_DATE_FIELD_FORMAT];
}

export function formatDateField(date: Date, fmt: GQL.DateFieldFormat | null | undefined): string {
  return format(date, getDateFieldFormatString(fmt));
}

/**
 * Falls back only to the legacy default so text written before the per-field
 * format existed still parses. A wider fallback would silently reinterpret
 * ambiguous numeric strings (e.g. `01/02/2026` matches both US and UK slash)
 * and swap day and month. A year below 1000 means a two-digit year was read
 * by a four-digit token (`26` as year 26), so that parse is rejected.
 */
export function parseDateField(
  text: string,
  fmt: GQL.DateFieldFormat | null | undefined
): Date | null {
  const primary = fmt ?? DEFAULT_DATE_FIELD_FORMAT;
  const candidates =
    primary === DEFAULT_DATE_FIELD_FORMAT ? [primary] : [primary, DEFAULT_DATE_FIELD_FORMAT];
  for (const candidate of candidates) {
    const parsed = parse(text, FORMAT_STRINGS[candidate], new Date());
    if (isValid(parsed) && parsed.getFullYear() >= MIN_PARSED_YEAR) return parsed;
  }
  return null;
}

/**
 * Derives a Date field's ISO value from its placed text. Text that is the
 * stored date in any known format keeps the stored date, before any parse: a
 * format switch writes the text and the format in two steps, and parsing the
 * new text with the old format reads `02/01/2026` as 1 February where the
 * stored 2 January was only reformatted. The picker writes the ISO value
 * itself, so a picked date never depends on this parse.
 */
export function resolveDateFieldIsoValue(options: {
  /** The field's stored format; null means the legacy default. */
  dateFormat: GQL.DateFieldFormat | null | undefined;
  /** The field's stored ISO date, if any. */
  storedValue: string | null | undefined;
  /** The text placed in the document for the field. */
  text: string;
}): string | null {
  const { dateFormat, storedValue, text } = options;
  const storedDate = storedValue ? parseISO(storedValue) : null;
  const isStoredDateInText =
    storedDate &&
    isValid(storedDate) &&
    Object.values(GQL.DateFieldFormat).some(fmt => formatDateField(storedDate, fmt) === text);
  if (isStoredDateInText) return getISO8601DateString(storedDate);

  const parsed = parseDateField(text, dateFormat);
  return parsed ? getISO8601DateString(parsed) : null;
}

export function previewDateFieldFormat(fmt: GQL.DateFieldFormat): string {
  return formatDateField(PREVIEW_DATE, fmt);
}
